import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles } from '@material-ui/core/styles';
import { green, blue } from '@material-ui/core/colors';
import { useLocation } from 'react-router-dom';
import Box from '@material-ui/core/Box';
import TableRow from '@material-ui/core/TableRow';
import TableCell from '@material-ui/core/TableCell';
import IconButton from '@material-ui/core/IconButton';
import Typography from '@material-ui/core/Typography';
import CircularProgress from '@material-ui/core/CircularProgress';
import * as Mui from '@material-ui/core';
import * as MuiIcons from '@material-ui/icons';
import ModalImprimirOrcamento from '~/pages/TelaVenda/modal/ModalImprimirOrcamento';
import ModalAssinatura from '~/pages/TelaVenda/modal/ModalAssinatura';
import VisibilityIcon from "@mui/icons-material/Visibility";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import VerifiedIcon from '@mui/icons-material/Verified';

import api from '~/services/api';
import history from '~/services/history';
import toast from '~/services/toast';
import * as monitorActions from '~/store/modules/monitor/monitor-actions';
import * as pdvActions from '~/store/modules/pdv/pdv-actions';
import { getApplicationIsOnline } from '~/store/modules/app/app-selectors';
import ModalConfirmacao from '../../modal/ModalConfirmacao';
import { formatCurrency, formatDate } from '~/utils/format';
import { selectorParametroAsBoolean, selectorRotina } from '~/store/modules/auth/auth-selectors';
import { API_BASE_URL, API_LOCAL_URL, TIPO_DOCUMENTO, APP_ERROR, ORCAMENTO_SITUACAO, ASSINATURA_TIPO_DOCUMENTO, ASSINATURA_MODAL_TIPO_OPERACAO } from '~/constants';

import Label from '~/components/Label';
import { OrcamentoOnlineService } from '~/store/modules/orcamento/orcamento.service';
const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    alignItems: 'center'
  },
  wrapper: {
    margin: theme.spacing(1),
    position: 'relative'
  },
  badgeOffline: {
    backgroundColor: '#ccc',
    color: 'white'
  },
  badgeOnline: {
    backgroundColor: '#4caf50',
    color: 'white'
  },
  buttonPrimary: {
    backgroundColor: blue[500],
    '&:hover': {
      backgroundColor: blue[700]
    }
  },
  buttonSuccess: {
    backgroundColor: green[500],
    '&:hover': {
      backgroundColor: green[700]
    }
  },
  fabProgress: {
    color: green[500],
    position: 'absolute',
    top: -4,
    left: -4,
    zIndex: 1
  },
  buttonProgress: {
    color: green[500],
    position: 'absolute',
    top: '50%',
    left: '50%',
    marginTop: -12,
    marginLeft: -12
  }
}));

const getSituacaoLabel = situacao => {
  const colors = ['red', 'green', 'yellow'];
  return <Label color={colors[situacao] ?? 'grey'}>{ORCAMENTO_SITUACAO[situacao] ?? '-'}</Label>;
};

const OrcamentoTableRow = React.memo(({ data, isSelected, onRefresh, ...props }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const location = useLocation();

  const { sendingRows } = useSelector(state => state.monitor);
  const { terminal } = useSelector(state => state.app);
  const isOnline = useSelector(getApplicationIsOnline());
  const [anchorEl, setAnchorEl] = useState(null);

  const parametroPC009 = useSelector(selectorParametroAsBoolean('PC009'))

  const permissaoUsuarioCancelarOrcamento = useSelector(selectorRotina('F050ORC'));

  const permissaoUsuarioVerAssinatura = useSelector(selectorRotina('A040ASS')); // Veja as assinaturas dos documentos
  const permissaoUsuarioAdicionarAssinatura = useSelector(selectorRotina('A010ASS')); // Adicione assinaturas aos documentos
  const permissaoUsuarioRefazerAssinatura = useSelector(selectorRotina('A020ASS')); // Refaça as assinaturas dos documentos
  const permissaoUsuarioExcluirAssinatura = useSelector(selectorRotina('A030ASS')); // Exclua as assinaturas dos documentos

  const [showModalImpressao, setShowModalImpressao] = useState({
    show: false,
    separaPorAmbiente: false
  });
  const [showModalCancelamento, setShowModalCancelamento] = useState({ open: false });

  const [modalAssinatura, setModalAssinatura] = useState({
    open: false,
    tipoDocumento: null,
    numeroDocumento: null,
    documentoId: null,
    assinaturaSalva: null,
    somenteLeitura: false,
    assinaturaModalTipoOperacao: null,
  });

  const isDocumentoOffline = !data?.id && (!data?.hasSynced || data?.hasSynced === false);
  const documentoId = isDocumentoOffline ? data?._id : data?.id;

  const naoPermiteCancelarOrcamento = data?.cancelado || data?.situacao != 1

  const handleEnviarOrcamento = () => {
    dispatch(monitorActions.sendOrcamentoRequest(data));
  };

  const handleImprimirOrcamento = async data => {
    const toastInstance = toast.loading(`Carregando, aguarde...`);
    try {
      const { data: orcamento } = await api.get(
        data?.isOffline
          ? `${API_LOCAL_URL}/faturamento/orcamento/${data?._id}`
          : `${API_BASE_URL}/v1/faturamento/orcamento/${data?.id}`
      );

      dispatch(monitorActions.printDocumento(TIPO_DOCUMENTO.ORCAMENTO, orcamento));
    } catch (error) {
      dispatch({ type: APP_ERROR, error });
    } finally {
      toastInstance.close();
    }
  };

  const handleEditarOrcamento = async data => {
    const toastInstance = toast.loading(`Carregando, aguarde...`);
    if(data.situacao === 0) return toast.warning('Não é possível editar com situação fechada!');
    try {
      const { data: orcamento } = await api.get(
        data?.isOffline
          ? `${API_LOCAL_URL}/faturamento/orcamento/${data?._id}`
          : `${API_BASE_URL}/v1/faturamento/orcamento/${data?.id}`
      );

      history.push('/pdv', {
        tipoDocumento: TIPO_DOCUMENTO.ORCAMENTO,
        venda: { ...orcamento, editar: true }
      });
    } catch (error) {
      dispatch({ type: APP_ERROR, error });
    } finally {
      toastInstance.close();
    }
  };

  const handleDeletarOrcamento = async data => {
    const { isConfirmed } = await toast.confirm(
      'Deseja excluir este registro?',
      'Certifique-se de que realmente deseja fazer isso, pois esta ação é irreversível.',
      {
        icon: 'danger',
        confirmButtonText: 'Excluir',
        cancelButtonText: 'Cancelar'
      }
    );
    if (isConfirmed) {
      const toastInstance = toast.loading(`Aguarde...`);
      try {
        await api.delete(
          data?.isOffline
            ? `${API_LOCAL_URL}/faturamento/orcamento/${data?._id}`
            : `${API_BASE_URL}/v1/faturamento/orcamento/${data?.id}`
        );
        dispatch(monitorActions.loadOrcamentosRequest());
      } catch (error) {
        dispatch({ type: APP_ERROR, error });
      } finally {
        toastInstance.close();
      }
    }
  };

  const handleCancelarOrcamento = async orcamento => {
    if (!permissaoUsuarioCancelarOrcamento) {
      toast.warning('Usuário não tem permissão de cancelar orçamento!');
      return
    }
    setShowModalCancelamento({
      open: true,
      rotina: 'F050ORC',
      title: 'Deseja cancelar este orçamento?',
      body: 'Certifique-se de que realmente deseja fazer isso, pois esta ação é irreversível.',
      options: {
        icon: 'warning',
        confirmButtonText: 'Confirma cancelamento do orçamento',
        cancelButtonText: 'Voltar'
      },
      onClose: () => {
        setShowModalCancelamento({ open: false });
      },
      onCloseCallback: async tokenLiberacaoRotina => {
        const toastInstance = toast.loading(`Aguarde...`);
        try {
          setShowModalCancelamento({ open: false });
          const orcamentoService = new OrcamentoOnlineService();
          await orcamentoService.cancelarOrcamento([orcamento.id], tokenLiberacaoRotina);

          dispatch(monitorActions.loadOrcamentosRequest());

          toast.success('Operação realizada com sucesso!');
        } catch (error) {
          dispatch({ type: APP_ERROR, error });
        } finally {
          toastInstance.close();
        }
      }
    });
  };

  const handleImportarOrcamento = async data => {
    dispatch(pdvActions.importarOrcamento([data], location?.pathname, () => {}));
  };

  const handleOpenMenu = event => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  return (
    <>
    <TableRow hover {...props} height={48}>
      <Mui.TableCell align="center">
        {data?.isOffline && !data?.id ? (
          <Mui.Tooltip title="Offline">
            <Mui.Badge
              classes={{ badge: classes.badgeOffline }}
              overlap="circular"
              badgeContent={''}
            />
          </Mui.Tooltip>
        ) : (
          <Mui.Tooltip title="Online">
            <Mui.Badge
              classes={{ badge: classes.badgeOnline }}
              overlap="circular"
              badgeContent={''}
            />
          </Mui.Tooltip>
        )}
      </Mui.TableCell>
      <TableCell>
        <Typography variant="h6">
          {data?.serie}/{data?.numero}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography variant="h6">{formatDate(new Date(data?.dataEmissao))}</Typography>
      </TableCell>
      <Mui.TableCell>{getSituacaoLabel(data?.situacao)}</Mui.TableCell>
      <TableCell>{data?.fatCondicaopagamento?.descricao ?? '-'}</TableCell>
      <TableCell>{data?.finMeiopagamento?.descricao ?? '-'}</TableCell>

      <TableCell>
        <Typography variant="h6">{data?.clienteNome ?? '-'}</Typography>
      </TableCell>
      <TableCell>
        <Typography variant="h6">{data?.fatVendedor?.nome}</Typography>
      </TableCell>
      <TableCell align="right">
        <Typography variant="h6">{formatCurrency(data?.valorTotal)}</Typography>
      </TableCell>

      <Mui.TableCell style={{ width: 140 }}>
        <Mui.Box display="flex" flexDirection="row" justifyContent="flex-end">
          {data?.isOffline && !data?.id && isOnline && (
            <div className={classes.root}>
              <div className={classes.wrapper}>
                <IconButton size="small" onClick={() => handleEnviarOrcamento()}>
                  <>
                    {sendingRows && sendingRows[data?._id]?.loading ? (
                      <>
                        <MuiIcons.CloudUpload />
                        <CircularProgress size={38} className={classes.fabProgress} />
                      </>
                    ) : (
                      <MuiIcons.CloudUpload />
                    )}
                  </>
                </IconButton>
              </div>
            </div>
          )}

          <Mui.IconButton onClick={() => handleImprimirOrcamento(data)}>
            <MuiIcons.Print fontSize="small" />
          </Mui.IconButton>

          <Mui.Tooltip title="Mais ações">
            <Mui.IconButton
              disabled={terminal === null}
              aria-controls="simple-menu"
              aria-haspopup="true"
              aria-label={'Mais ações'}
              onClick={handleOpenMenu}
            >
              <MuiIcons.MoreVert />
            </Mui.IconButton>
          </Mui.Tooltip>

          <Mui.Menu
            id="simple-menu"
            anchorEl={anchorEl}
            keepMounted
            open={Boolean(anchorEl)}
            onClose={handleCloseMenu}
          >

              <Mui.MenuItem onClick={() => handleEditarOrcamento(data)}>
                <MuiIcons.Edit fontSize="small" /> <span style={{ marginLeft: 12 }}>Editar</span>
              </Mui.MenuItem>
              <Mui.MenuItem onClick={() => handleDeletarOrcamento(data)}>
                <MuiIcons.Delete fontSize="small" /> <span style={{ marginLeft: 12 }}>Deletar</span>
              </Mui.MenuItem>
              <Mui.Divider style={{ marginTop: "4px", marginBottom: "4px" }}/>

              {(permissaoUsuarioVerAssinatura && permissaoUsuarioAdicionarAssinatura && data?.intAssinaturaId == null) && (
                <Mui.MenuItem
                  onClick={() =>
                    setModalAssinatura({
                      open: true,
                      tipoDocumento: ASSINATURA_TIPO_DOCUMENTO.ORCAMENTO,
                      numeroDocumento: data?.numero ?? '-',
                      documentoId: data?.id ?? null,
                      assinaturaSalva: null,
                      somenteLeitura: false,
                      assinaturaModalTipoOperacao: ASSINATURA_MODAL_TIPO_OPERACAO.ADICIONAR,
                    })
                  }
                >
                  <VerifiedIcon fontSize="small" />
                  <span style={{ marginLeft: 12 }}>
                    Assinatura
                  </span>
                  <AddIcon
                    sx={{
                      fontSize: 17,
                      mt: "-10px",
                      ml: 0.2,
                    }}
                  />
                </Mui.MenuItem>
              )}

              {(permissaoUsuarioVerAssinatura && permissaoUsuarioRefazerAssinatura && data?.intAssinaturaId != null) && (
                <Mui.MenuItem
                  onClick={() =>
                    setModalAssinatura({
                      open: true,
                      tipoDocumento: ASSINATURA_TIPO_DOCUMENTO.ORCAMENTO,
                      numeroDocumento: data?.numero ?? '-',
                      documentoId: data?.id ?? null,
                      assinaturaSalva: data?.intAssinatura?.assinatura ?? null,
                      somenteLeitura: false,
                      assinaturaModalTipoOperacao: ASSINATURA_MODAL_TIPO_OPERACAO.EDITAR,
                    })
                  }
                >
                  <VerifiedIcon fontSize="small" />
                  <span style={{ marginLeft: 12 }}>
                    Assinatura
                  </span>
                  <EditIcon
                    sx={{
                      fontSize: 13,
                      mt: "-10px",
                      ml: 0.5,
                    }}
                  />
                </Mui.MenuItem>
              )}

              {(permissaoUsuarioVerAssinatura && permissaoUsuarioExcluirAssinatura && data?.intAssinaturaId != null) && (
                <Mui.MenuItem
                  onClick={() =>
                    setModalAssinatura({
                      open: true,
                      tipoDocumento: ASSINATURA_TIPO_DOCUMENTO.ORCAMENTO,
                      numeroDocumento: data?.numero ?? '-',
                      documentoId: data?.id ?? null,
                      assinaturaSalva: data?.intAssinatura?.assinatura ?? null,
                      somenteLeitura: true,
                      assinaturaModalTipoOperacao: ASSINATURA_MODAL_TIPO_OPERACAO.REMOVER,
                    })
                  }
                >
                  <VerifiedIcon fontSize="small" />
                  <span style={{ marginLeft: 12 }}>
                    Assinatura
                  </span>
                  <DeleteIcon
                    sx={{
                      fontSize: 13,
                      mt: "-10px",
                      ml: 0.4,
                    }}
                  />
                </Mui.MenuItem>
              )}

              <Mui.MenuItem onClick={() => handleImportarOrcamento(data)}>
                <MuiIcons.ImportExport fontSize="small" /> <span style={{ marginLeft: 12 }}>Exportar</span>
              </Mui.MenuItem>

              {permissaoUsuarioCancelarOrcamento &&
                <Mui.Tooltip title={(
                  naoPermiteCancelarOrcamento
                ) ? <>
                  { naoPermiteCancelarOrcamento ? 'Orçamento não pode ser cancelado!' : ''}
                </> : ''}>
                  <span>
                    <Mui.MenuItem disabled={naoPermiteCancelarOrcamento} onClick={() => handleCancelarOrcamento(data)}>
                      <MuiIcons.Cancel fontSize="small" /> <Mui.Typography style={{ marginLeft: 12 }}>Cancelar</Mui.Typography>
                    </Mui.MenuItem>
                  </span>
                </Mui.Tooltip>
              }

              <Mui.Divider style={{ marginTop: "4px", marginBottom: "4px" }}/>

              <Mui.MenuItem onClick={() => setShowModalImpressao({ show: true, separaPorAmbiente: false })}>
                <MuiIcons.Print fontSize="small" /> <span style={{ marginLeft: 12 }}> Imprimir</span>
              </Mui.MenuItem>

              { parametroPC009 &&
                <Mui.MenuItem onClick={() => setShowModalImpressao({ show: true, separaPorAmbiente: true })}>
                  <MuiIcons.Print fontSize="small" /> <span style={{ marginLeft: 12 }}> Ambiente</span>
                </Mui.MenuItem>
              }

            </Mui.Menu>
          </Mui.Box>
        </Mui.TableCell>
      </TableRow>
      {showModalCancelamento?.open && <ModalConfirmacao {...showModalCancelamento} />}
      {showModalImpressao.show && (
        <ModalImprimirOrcamento
          open={showModalImpressao.show}
          separaPorAmbiente={showModalImpressao.separaPorAmbiente}
          orcamento={data}
          onClose={() => {
            setShowModalImpressao({ show: false, separaPorAmbiente: false });
          }}
        />
      )}
      {modalAssinatura.open && (
        <ModalAssinatura
          {...modalAssinatura}
          onClose={resultado => {
              setModalAssinatura(prev => ({
                  ...prev,
                  open: false
              }));
              if (resultado && resultado.confirmado) {
                  data.intAssinaturaId = (resultado.novaAssinatura && resultado.novaAssinatura.id) ? resultado.novaAssinatura.id : null;
                  data.intAssinatura = resultado.novaAssinatura ? resultado.novaAssinatura : null;
                  setAnchorEl(null);
              }
          }}
        />
      )}
    </>
  );
});

export default OrcamentoTableRow;
